/**
 * Audit/mapping exports. Everything is generated client-side and downloaded as text.
 * These files contain the ORIGINAL identifiers; users are warned to store them separately.
 */
import type { BatchResult } from "./types";

function csvEscape(s: string): string {
  const needs = /[",\n\r]/.test(s);
  const v = s.replace(/"/g, '""');
  return needs ? `"${v}"` : v;
}

/** Per-file, per-tag change log. */
export function auditCsv(result: BatchResult): string {
  const rows: string[] = ["file,output,tag,name,action,before,after"];
  for (const f of result.files) {
    for (const a of f.audit) {
      rows.push([f.name, f.outputName, a.tag, a.name, a.action, a.before, a.after].map(csvEscape).join(","));
    }
    if (!f.ok) rows.push([f.name, "", "", "", "ERROR", f.error ?? "", ""].map(csvEscape).join(","));
  }
  return rows.join("\r\n") + "\r\n";
}

/** Summary only: one line per file, useful for IRB submissions. */
export function summaryCsv(result: BatchResult): string {
  const rows: string[] = ["file,output,status,changes"];
  for (const f of result.files) {
    rows.push([f.name, f.outputName, f.ok ? "OK" : `ERROR: ${f.error ?? ""}`, String(f.audit.length)].map(csvEscape).join(","));
  }
  return rows.join("\r\n") + "\r\n";
}

/** Original → pseudonym mapping (Pro). Keep this file private: it re-identifies patients. */
export function mappingCsv(result: BatchResult): string {
  const rows: string[] = ["type,original,replacement"];
  for (const [k, v] of Object.entries(result.patientMap)) rows.push(["patient", k, v].map(csvEscape).join(","));
  for (const [k, v] of Object.entries(result.uidMap)) rows.push(["uid", k, v].map(csvEscape).join(","));
  rows.push(["date_shift_days", "", String(result.dateShiftDays)].map(csvEscape).join(","));
  return rows.join("\r\n") + "\r\n";
}

/** Human-readable methods statement for papers/IRB. */
export function methodsStatement(result: BatchResult): string {
  const o = result.options;
  const opts: string[] = [];
  if (o.modifyDates) opts.push("Retain Longitudinal Temporal Information with Modified Dates (113107)");
  if (o.retainPatientCharacteristics) opts.push("Retain Patient Characteristics (113108)");
  if (o.retainDeviceIdentity) opts.push("Retain Device Identity (113109)");
  if (o.retainInstitutionIdentity) opts.push("Retain Institution Identity (113112)");
  if (o.retainUIDs) opts.push("Retain UIDs (113110)");
  if (o.retainPrivateTags) opts.push("Retain Safe Private (113111)");
  if (o.keepDescriptors) opts.push("Clean Descriptors (113105)");
  const n = result.files.filter((f) => f.ok).length;
  return [
    `DICOM de-identification statement`,
    ``,
    `${n} DICOM object(s) were de-identified with DicomScrub (browser-based, no data transmitted) following the`,
    `DICOM PS3.15 Annex E Basic Application Level Confidentiality Profile${opts.length ? " with the following options: " + opts.join("; ") : ""}.`,
    `Private data elements were ${o.retainPrivateTags ? "retained" : "removed"}. Unique identifiers were ${o.retainUIDs ? "retained" : "replaced with newly generated UIDs, consistently across the batch"}.`,
    `Patient identifiers were replaced with sequential pseudonyms (${o.patientIdPrefix}nnn).`,
    o.modifyDates ? `All dates were shifted by a constant offset per batch; the offset is held by the data custodian.` : `Dates were removed or replaced with dummy values.`,
    `The attributes Patient Identity Removed (0012,0062) = YES and De-identification Method (0012,0063/0064) were recorded in every object.`,
  ].join("\n");
}
